<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="isAdmin"
  >
    <ReportTable minWidth="1080px">
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>Gross production</span></th>
          <th><span>Adjustments</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Estimated writeoffs"
              :tooltip="tooltips['Estimated writeoffs']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Net production"
              :tooltip="tooltips['Net production']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Per procedure"
              :tooltip="tooltips['Per procedure']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Per hour"
              :tooltip="tooltips['Per hour']"
            />
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <!-- clientAdmin view -->
        <template v-if="isAdmin && productionPerProvider.length">
          <tr
            v-for="(item, index) in productionPerProvider"
            :key="index"
            :class="{
              active: item.isCurrentUserRecord,
              median: item.isMedian,
              hidden: !item.isActive || item.isHidden || item.isHiddenReport,
            }"
          >
            <td>{{ item.fullName }}</td>
            <td>{{ formatMoney(item.grossProduction) }}</td>
            <td>{{ formatMoney(item.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(item.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(item.netProduction) }}</td>
            <td>{{ formatMoney(item.netProductionPerProcedure) }} ({{ item.productionUniqueCompletedProcedures }})</td>
            <td>{{ formatMoney(item.netProductionPerHour) }} {{ formatMinutes(item.productiveMinutes) }}</td>
          </tr>

          <tr
            v-if="!userHasRecordInReport"
            class="median"
          >
            <td>Median</td>
            <td>{{ formatMoney(median.grossProduction) }}</td>
            <td>{{ formatMoney(median.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(median.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(median.netProduction) }}</td>
            <td>{{ formatMoney(median.netProductionPerProcedure) }} ({{ median.productionUniqueCompletedProcedures }})
            </td>
            <td>{{ formatMoney(median.netProductionPerHour) }} {{ formatMinutes(median.productiveMinutes) }}</td>
          </tr>

          <tr>
            <td>Whole clinic</td>
            <td>{{ formatMoney(clinicMetricsByName.grossProduction) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.netProduction) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.netProductionPerProcedure) }} ({{
              clinicMetricsByName.productionUniqueCompletedProcedures }})</td>
            <td>{{ formatMoney(clinicMetricsByName.netProductionPerHour) }} {{
              formatMinutes(clinicMetricsByName.productiveMinutes) }}</td>
          </tr>
        </template>

        <!-- clientUser view -->
        <template v-else-if="!isAdmin && userRecord">
          <tr class="active">
            <td>{{ userRecord.fullName }}</td>
            <td>{{ formatMoney(userRecord.grossProduction) }}</td>
            <td>{{ formatMoney(userRecord.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(userRecord.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(userRecord.netProduction) }}</td>
            <td>{{ formatMoney(userRecord.netProductionPerProcedure) }} ({{
              userRecord.productionUniqueCompletedProcedures }})
            </td>
            <td>{{ formatMoney(userRecord.netProductionPerHour) }} {{ formatMinutes(userRecord.productiveMinutes) }}
            </td>
          </tr>

          <tr v-if="median">
            <td>Median</td>
            <td>{{ formatMoney(median.grossProduction) }}</td>
            <td>{{ formatMoney(median.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(median.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(median.netProduction) }}</td>
            <td>{{ formatMoney(median.netProductionPerProcedure) }} ({{ median.productionUniqueCompletedProcedures }})
            </td>
            <td>{{ formatMoney(median.netProductionPerHour) }} {{ formatMinutes(median.productiveMinutes) }}</td>
          </tr>

          <tr v-if="averageTop10Percent">
            <td>Average top 10 percent</td>
            <td>{{ formatMoney(averageTop10Percent.grossProduction) }}</td>
            <td>{{ formatMoney(averageTop10Percent.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(averageTop10Percent.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(averageTop10Percent.netProduction) }}</td>
            <td>{{ formatMoney(averageTop10Percent.netProductionPerProcedure) }} ({{
              averageTop10Percent.productionUniqueCompletedProcedures }})</td>
            <td>{{ formatMoney(averageTop10Percent.netProductionPerHour) }} {{
              formatMinutes(averageTop10Percent.productiveMinutes) }}</td>
          </tr>

          <tr v-if="clinicMetricsByName">
            <td>Whole clinic</td>
            <td>{{ formatMoney(clinicMetricsByName.grossProduction) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.completedProceduresAdjustment) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.estimatedWriteoff) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.netProduction) }}</td>
            <td>{{ formatMoney(clinicMetricsByName.netProductionPerProcedure) }} ({{
              clinicMetricsByName.productionUniqueCompletedProcedures }})</td>
            <td>{{ formatMoney(clinicMetricsByName.netProductionPerHour) }} {{
              formatMinutes(clinicMetricsByName.productiveMinutes) }}</td>
          </tr>
        </template>

        <NoRecordsFound
          v-else
          colspan="7"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'RDHProduction',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      productionPerProviderAll: [],
      clinicMetricsByName: null,
      userRole: '',
      userRecord: null,
      averageTop10Percent: null,
      median: null,
      loading: false,
      tooltips: {
        'Estimated writeoffs':
          'Estimated at the point of setting a procedure complete, not when the insurance has paid their portion and the payment is entered',
        'Net production': 'Gross production + Adjustments - Estimated writeoffs',
        'Per procedure': 'Net production per completed procedure',
        'Per hour': 'Net production per scheduled hour',
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'clientAdmin';
    },
    userHasRecordInReport() {
      return !!this.userRecord;
    },
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    productionPerProvider() {
      return this.showHiddenProviders
        ? this.productionPerProviderAll
        : this.productionPerProviderAll.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getRDHProductionReport({ start, end });

      if (result.status === 200) {
        const productionPerProvider = result.data.productionPerProvider ?? [];
        this.productionPerProviderAll = productionPerProvider;

        Utils.prependCurrentUserRecordAndMedian(productionPerProvider, result.data.median);

        this.clinicMetricsByName = result.data.clinicMetricsByName;
        this.userRecord = result.data.userRecord;
        this.averageTop10Percent = result.data.averageTop10Percent;
        this.median = result.data.median;
      } else {
        this.productionPerProviderAll = [];
      }

      this.loading = false;
    },
    formatMoney(number) {
      return Utils.formatCurrency(number, false, false);
    },
    formatMinutes(minutes) {
      const hours = Utils.formatMinutes(minutes);
      return hours ? ` (${hours})` : '';
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
  mounted() {
    this.userRole = DataService.getRole();
  },
};
</script>
